import { createRouter, createWebHistory } from "vue-router";
import { RouteNames } from "./RouteNames";
import { useUserStore } from "@/stores/UserStore";
import { swalHomemover } from "@/alerts/swalHomemover";
import { useUserProgressStore } from '@/stores/UserProgressStore';
import { ProgressEnum } from '@/types/progress/ProgressEnum';
import { NavigateUserPreviousPage } from "./NavigateUserPreviousPage";
import { useApplicationStore } from "@/stores/ApplicationStore";
import { isStagingEnvironment } from "@/helpers/environment/environmentReader";

function loadView(view: string) {
  return () => import(/* webpackChunkName: "login" */ `../views/${view}.vue`)
}

const router = createRouter({
  routes: [
    {
      path: "/home/",
      name: RouteNames.Home,
      meta: {
        requiresAuth: true,
      },
      component: loadView('home-page')
    },
    {
      path: '/test',
      name: RouteNames.Test,
      meta: {
        requiresAuth: true
      },
      component: loadView('test-page')
    },
    {
      path: '/quiz',
      name: RouteNames.Quiz,
      meta: {
        requiresAuth: true
      },
      component: loadView('quiz-page')
    },
    {
      path: '/loading',
      name: RouteNames.LoadingPage,
      meta: {
        requiresAuth: true
      },
      component: loadView('loading-page')
    },
    {
      path: '/plans/energy',
      name: RouteNames.EnergyPlanSelection,
      meta: {
        requiresAuth: true
      },
      component: loadView('energy-plan-selection')
    },
    {
      path: '/plans/broadband',
      name: RouteNames.BroadbandPlanSelection,
      meta: {
        requiresAuth: true
      },
      component: loadView('broadband-plan-selection')
    },
    {
      path: '/plans/tv',
      name: RouteNames.TvPlanSelection,
      meta: {
        requiresAuth: true
      },
      component: loadView('tv-plan-selection')
    },
    {
      path: '/plans/minibox',
      name: RouteNames.MiniBoxSelection,
      meta: {
        requiresAuth: true
      },
      component: loadView('mini-box-selection')
    },
    {
      path: '/plans/streaming',
      name: RouteNames.StreamingSelection,
      meta: {
        requiresAuth: true
      },
      component: loadView('streaming-plan-selection')
    },
    {
      path: '/plans/sim',
      name: RouteNames.SimSelection,
      meta: {
        requiresAuth: true
      },
      component: loadView('sim-cards-selection')
    },
    {
      path: '/plans/waste',
      name: RouteNames.WastePlans,
      meta: {
        requiresAuth: true
      },
      component: loadView('waste-plans-page')
    },
    {
      path: '/plans/security',
      name: RouteNames.SecurityPlans,
      meta: {
        requiresAuth: true
      },
      component: loadView('security-plans-page')
    },
    {
      path: '/plans/securityaddon',
      name: RouteNames.SecurityPlansAddon,
      meta: {
        requiresAuth: true
      },
      component: loadView('security-plans-addon-page')
    },
    {
      path: '/review/marketing',
      name: RouteNames.MarketingPreferanceReview,
      meta: {
        requiresAuth: true
      },
      component: loadView('marketing-preference-review')
    },
    {
      path: '/order-summary',
      name: RouteNames.OrderSummary,
      meta: {
        requiresAuth: true
      },
      component: loadView('order-summary-page')
    },
    {
      path: '/order-status',
      name: RouteNames.OrderStatus,
      meta: {
        requiresAuth: true
      },
      component: loadView('order-status-page')
    },
    {
      path: '/renewal/property',
      name: RouteNames.RenewalProperty,
      meta: {
        requiresAuth: true
      },
      component: loadView('renewal-your-property')
    },
    {
      path: '/renewal/contract',
      name: RouteNames.RenewalExistingContract,
      meta: {
        requiresAuth: true
      },
      component: loadView('renewal-existing-contract')
    },
    {
      path: '/renewal/meterreadings',
      name: RouteNames.RenewalMeterRead,
      meta: {
        requiresAuth: true
      },
      component: loadView('renewal-meter-readings')
    },
    {
      path: '/renewal/movedaddress',
      name: RouteNames.RenewalMovedProperty,
      meta: {
        requiresAuth: true
      },
      component: loadView('renewal-moved-address-error')
    },
    {
      path: '/mymove/security',
      name: RouteNames.MyMoveSecurity,
      meta: {
        requiresAuth: true
      },
      component: loadView('my-move-security')
    },
        {
      path: '/mymove/confirmation',
      name: RouteNames.MyMoveConfirmation,
      meta: {
        requiresAuth: true
      },
      component: loadView('my-move-confirmation')
    },
    {
      path: '/error-page',
      name: RouteNames.ErrorPage,
      meta: {
        requiresAuth: true
      },
      component: loadView('error-page')
    },
    {
      path: "/",
      redirect: "/home"
    },
    {
      path: "/recovery",
      redirect: "/home"
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/home"
    }
  ],
  history: createWebHistory(),
  // Scrolls the user to the top of the screen when navigating to a new page/route
  scrollBehavior() {
    return { top: 0 };
  },
});

  /* To prevent live submission in staging/production environments, 
   * inspect the query string to see if user is running the app in test mode */
router.beforeEach(to => {
  const applicationStore = useApplicationStore();

  // Staging environment should always run in test mode to prevent live submission through production salesforce org
  if (isStagingEnvironment()) {
    applicationStore.isTestMode = true;
    return;
  }

  /* For all other environments, retrieve test mode from the query string parameter
   * If it's not present, then don't set the variable. 
   * This prevents test mode getting reset when navigating through the app */
  if (to?.query?.testmode) {
    applicationStore.isTestMode = to?.query?.testmode == 'true';
  }
});

router.beforeEach(() => {
  // When navigating to a new route, ensure any sweetalerts from the previous route have been closed
  if (swalHomemover.isVisible()) {
    swalHomemover.close();
  }
});

router.beforeEach(async (to, from) => {
  //I thought I would not run this code here for the home page as it didn't seem to work for the it - as a test I fetched the user progress at 
  // the start of the home page's onMounted funtion and it errord so I left in the code that fetched the id in the home page.

  if (to.name !== RouteNames.Home) {
    const userStore = useUserStore();
    const userProgressStore = useUserProgressStore();
    let userId: string | undefined

    userId = router.currentRoute.value.query.userid?.toString();

    if (!router.currentRoute.value.query.userid?.toString()) {
      userId = userProgressStore.getUserIdFromCookie();
    }

    if (userId) {
      userProgressStore.setUserId(userId);
      userStore.updateUserId(userId);
      await userProgressStore.initUserProgressStore();

    }
  }
});

/* Navigate the user back to the last step they were on if they try to manually go to 
 * a page that they can't access yet */
router.beforeEach(async (to, from) => {
  /* If user is navigating within the app then all
   * Also need to ignore the home page as it has it's own navigation check */
  if (from.name || to.name === RouteNames.Home || to.name === RouteNames.Test) {
    return true
  }

  const userProgressStore = useUserProgressStore();

  /* If we can't get the user id from anywhere continue navigation 
   * as we won't be able to call the api to get the user's last step */
  if (!router.currentRoute.value.query.userid?.toString() && !userProgressStore.getUserIdFromCookie()) {
    return true
  }

  const userProgressStep = userProgressStore.getUserProgress().currentStep;

  switch (userProgressStep) {
    case ProgressEnum.Quiz:
      NavigateUserPreviousPage(String(to.name), RouteNames.Quiz);
      break;
    case ProgressEnum.LoadingPage:
      NavigateUserPreviousPage(String(to.name), RouteNames.LoadingPage);
      break;
    case ProgressEnum.EnergyPlan:
      NavigateUserPreviousPage(String(to.name), RouteNames.EnergyPlanSelection);
      break
    case ProgressEnum.BroadbandPlan:
      NavigateUserPreviousPage(String(to.name), RouteNames.BroadbandPlanSelection);
      break;
    case ProgressEnum.TvPlan:
      NavigateUserPreviousPage(String(to.name), RouteNames.TvPlanSelection);
      break;
    case ProgressEnum.MiniBox:
      NavigateUserPreviousPage(String(to.name), RouteNames.MiniBoxSelection);
      break;
    case ProgressEnum.StreamingPlan:
      NavigateUserPreviousPage(String(to.name), RouteNames.StreamingSelection);
      break;
    case ProgressEnum.SimCards:
      NavigateUserPreviousPage(String(to.name), RouteNames.SimSelection);
      break;
    case ProgressEnum.WastePlan:
      NavigateUserPreviousPage(String(to.name), RouteNames.WastePlans);
      break;
      case ProgressEnum.SecurityPlan:
        NavigateUserPreviousPage(String(to.name), RouteNames.SecurityPlans);
        break;
        case ProgressEnum.SecurityAddonPlan:
          NavigateUserPreviousPage(String(to.name), RouteNames.SecurityPlansAddon);
          break;
    case ProgressEnum.MarketingReview:
      NavigateUserPreviousPage(String(to.name), RouteNames.MarketingPreferanceReview);
      break;
    case ProgressEnum.OrderSummary:
      NavigateUserPreviousPage(String(to.name), RouteNames.OrderSummary);
      break;
    case ProgressEnum.Status:
      NavigateUserPreviousPage(String(to.name), RouteNames.OrderStatus);
      break;
    default: {
      return true;
    }
  }
});


// Ensure user's switch over is valid. If not, redirect them to the user status page
router.beforeEach(async (to) => {
  /* Avoid infinite loop by not checking switchover status on order status page
   * The home page is also ignored as it performs its own checks due to getting user id from the query string*/
  if (to.name === RouteNames.OrderStatus || to.name === RouteNames.Home || to.name === RouteNames.ErrorPage || to.name === RouteNames.Test) {
    return true;
  }

  const userStore = useUserStore();

  /* To prevent users double ordering energy and causing problems for Property Button
   * redirect the user to the Order Status page if they've already selected an energy deal */
  if (await userStore.hasUserSelectedEnergyDeal() && to.name?.toString().includes('my-move') == false ) {
    router.push({ name: RouteNames.OrderStatus, replace: true });
    return false;
  }
});

export default router;